import { useCallback, useMemo } from 'react';
import { AddToCartButton, Fragment, getColorVar, Icon, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import type { AddToCartButtonProps, AddToCartViewProps } from './add-to-cart.types';
import { Loader2, PhoneCall } from 'lucide-react';
import styles from './add-to-cart.module.css';
import { useTranslation } from 'next-i18next';
export const AddToCartView = ({
    productPrice,
    quantity,
    loading,
    disable,
    variant = 'quick',
    errorMessage,
    onAddClick,
    onBuyNow,
    onCallNow,
}: AddToCartViewProps) => {
    const { t } = useTranslation('flash-sale');
    const buttonStyleByVariant = useMemo(
        () => ({
            full: {
                addToCart: styles.addToCartFull,
                buyNow: styles.buyNowFull,
            },
            counter: {
                addToCart: styles.addToCartCounter,
                buyNow: styles.buyNowCounter,
            },
            quick: {
                addToCart: styles.addToCartQuick,
                buyNow: undefined,
            },
        }),
        []
    );

    const iconProps = useMemo(() => {
        return {
            size: 20,
            color: getColorVar('secondary', '0'),
        };
    }, []);

    const renderAddToCart = useCallback(() => {
        const shoppingCartIconDataUriProps = {
            src: '/assets/images/shopping-cart-icon.svg',
            alt: 'shopping-cart-icon',
        };
        return (
            <div className={styles.actionButton}>
                {loading && <Loader2 />}
                {!loading && (
                    <Fragment>
                        {variant !== 'counter' && <Icon type="data-uri" dataUriProps={shoppingCartIconDataUriProps} />}
                        <Text showIf={variant === 'full' || variant === 'counter'} className={styles.buttonLabel}>
                            {t('flash_sale_add_to_cart')}
                        </Text>
                    </Fragment>
                )}
            </div>
        );
    }, [loading, variant, t]);

    const addToCartButtonProps: AddToCartButtonProps['addToCartButtonProps'] = useMemo(() => {
        return {
            children: renderAddToCart(),
            onClick: onAddClick,
            disabled: disable,
            className: buttonStyleByVariant[variant].addToCart,
        };
    }, [disable, renderAddToCart, onAddClick, variant, buttonStyleByVariant]);

    const renderCallNowButton = useCallback(() => {
        return (
            <div className={styles.actionButton}>
                <PhoneCall {...iconProps} />
                <Text className={styles.buttonLabel}>{t('text_call')}</Text>
            </div>
        );
    }, [iconProps, t]);

    const callNowButtonProps: AddToCartButtonProps['callNowButtonProps'] = useMemo(() => {
        return {
            children: renderCallNowButton(),
            onClick: onCallNow,
            disabled: disable,
        };
    }, [disable, renderCallNowButton, onCallNow]);

    const renderBuyNowButton = useCallback(() => {
        return (
            <div className={styles.actionButton}>
                <Text className={styles.primaryText}>{t('flash_sale_buy_now')}</Text>
                <Text showIf={variant !== 'counter'} className={styles.secondaryText}>
                    {productPrice}
                </Text>
            </div>
        );
    }, [productPrice, t, variant]);

    const buyNowButtonProps: AddToCartButtonProps['buyNowButtonProps'] = useMemo(() => {
        return {
            children: renderBuyNowButton(),
            onClick: onBuyNow,
            disabled: disable,
            className: buttonStyleByVariant[variant].buyNow,
        };
    }, [disable, renderBuyNowButton, onBuyNow, variant, buttonStyleByVariant]);

    switch (variant) {
        case 'full':
            return (
                <AddToCartButton
                    variant="full"
                    cartQty={quantity}
                    addToCartButtonProps={addToCartButtonProps}
                    callNowButtonProps={callNowButtonProps}
                    // buyNowButtonProps={buyNowButtonProps}
                    error={errorMessage}
                />
            );
        case 'counter':
            return (
                <>
                    {/* update ui counter follow design  */}
                    <AddToCartButton
                        className={styles.containerCounter}
                        variant="full"
                        cartQty={quantity}
                        addToCartButtonProps={addToCartButtonProps}
                        buyNowButtonProps={buyNowButtonProps}
                        error={errorMessage}
                    />
                </>
            );
        case 'quick':
        default:
            return (
                <AddToCartButton
                    variant="quick"
                    className={styles.container}
                    cartQty={quantity}
                    addToCartButtonProps={addToCartButtonProps}
                />
            );
    }
};

/**
 *
 * <AddToCartButton
      className={styles.container}
      cartQty={quantity}
      disabled={disable}
      isAddToCartLoading={loading}
      variant={variant}
      onClick={onClick}
      iconColor={getColorVar('primary', '0')}
    />
 */
